import type { TaskSection } from '@contracts/task-query';

/**
 * Links into the Workplace List from elsewhere in the product (Điều hành, Mục tiêu, the partner register), and
 * the translation of the old `/tasks?…` links people bookmarked. The old list asked the server one question
 * per section; the Workplace answers the same question with its chips (spec §6.3), so each section maps to
 * exactly one chip: `today → today`, `blocked → blocked`, `soon → soon`, `important → important`.
 */

export type WorkplaceSearch = {
  section?: TaskSection;
  mine?: boolean;
  /** `user:<id>`; the old list's `external:<id>` is dropped, as a task is always given to an internal user. */
  recipient?: string;
  query?: string;
};

const SECTIONS: readonly TaskSection[] = ['today', 'blocked', 'soon', 'important'];
const RECIPIENT = /^user:[^:\s]+$/;

/** `/workplace?view=list&f=<section>[,mine]&rcpt=<recipient>` — the List, filtered as the old list was. */
export function buildWorkplaceSearch(q: WorkplaceSearch): string {
  const p = new URLSearchParams();
  p.set('view', 'list');
  const flags = [
    ...(q.section !== undefined && SECTIONS.includes(q.section) ? [q.section] : []),
    ...(q.mine === true ? ['mine'] : []),
  ];
  if (flags.length > 0) p.set('f', flags.join(','));
  if (q.recipient !== undefined && RECIPIENT.test(q.recipient)) p.set('rcpt', q.recipient);
  if (q.query) p.set('q', q.query);
  return `/workplace?${p.toString()}`;
}

/**
 * Where an old `/tasks?…` link lands (spec §6.1 `/tasks → /workplace?view=list`). `section`, `mine`,
 * `recipient` and `query` survive as chips, recipient and search; `status`, `priority`, `includeCompleted` and
 * `cursor` have no List equivalent and are dropped, as any unknown parameter is.
 */
export function translateLegacyTaskSearch(search: string): string {
  const params = new URLSearchParams(search);
  const section = params.get('section');
  return buildWorkplaceSearch({
    ...(section !== null && (SECTIONS as readonly string[]).includes(section) ? { section: section as TaskSection } : {}),
    ...(params.get('mine') === 'true' ? { mine: true } : {}),
    ...(params.get('recipient') !== null ? { recipient: params.get('recipient')! } : {}),
    ...(params.get('query') ? { query: params.get('query')! } : {}),
  });
}
